import { useEffect, useRef } from 'react';
import {
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  type MetricWithAttribution,
} from 'web-vitals/attribution';

interface CoreWebVitalsProps {
  cwvIndicator?: string;
}

type CollectionMode = 'traditional' | 'per_navigation';

interface MappedCwv {
  name: string;
  collectionMode: CollectionMode;
  debugTarget: string;
  testGroup: string;
  id: string;
  navigationId: number;
  navigationType: MetricWithAttribution['navigationType'];
  navigationUrl: string;
  value: number;
  delta: number;
  valueRounded: number;
  deltaRounded: number;
  attribution?: MetricWithAttribution['attribution'];
}

const getDebugTarget = (cwv: MetricWithAttribution) => {
  switch (cwv.name) {
    case 'CLS':
      return cwv.attribution.largestShiftTarget || '';
    case 'LCP':
      return cwv.attribution.target || '';
    case 'INP':
      return cwv.attribution.interactionTarget || '';
    default:
      return '';
  }
};

const resolveDebugMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const debugParam = urlParams.get('cwv_debug');
  let debugMode = debugParam === '1';

  try {
    if (debugParam === '1') {
      window.localStorage.setItem('cwv_debug', '1');
    } else if (debugParam === '0') {
      window.localStorage.removeItem('cwv_debug');
    } else {
      debugMode = window.localStorage.getItem('cwv_debug') === '1';
    }
  } catch (error) {
    console.warn(
      '[CWV] Unable to access the persisted debug preference; using the query-string setting for this page.',
      error
    );
  }

  return debugMode;
};

export default function CoreWebVitals({ cwvIndicator = '' }: CoreWebVitalsProps) {
  // Prevent duplicate event attachment
  const eventsAttached = useRef(false);

  useEffect(() => {
    if (eventsAttached.current) {
      return;
    }

    if (typeof window !== 'undefined') {
      const debugMode = resolveDebugMode();

      if (debugMode) {
        console.log(
          '[CWV] Debug mode enabled; data-layer pushes disabled. Use ?cwv_debug=0 to disable debug mode and resume data-layer pushes.'
        );

        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[CWV] Running in a development build; CSS loading causes extra layout shifts and slower timings. When evaluating CLS, use a production build (next build && next start) for representative values.'
          );
        }
      }

      const getTestGroup = () => cwvIndicator;

      const mapToDataLayer = (cwv: MetricWithAttribution, collectionMode: CollectionMode) => {
        window.dataLayer = window.dataLayer || [];

        const mappedCwv: MappedCwv = {
          name: cwv.name,
          collectionMode,
          debugTarget: getDebugTarget(cwv),
          testGroup: getTestGroup(),
          id: cwv.id,
          navigationId: cwv.navigationId,
          navigationType: cwv.navigationType,
          navigationUrl: cwv.navigationURL || window.location.href,
          value: cwv.value,
          delta: cwv.delta,
          valueRounded: Math.round(cwv.name === 'CLS' ? cwv.value * 1000 : cwv.value),
          deltaRounded: Math.round(cwv.name === 'CLS' ? cwv.delta * 1000 : cwv.delta),
        };

        // eslint-disable-next-line no-undef
        if (!debugMode) {
          window.dataLayer.push({
            event: 'coreWebVitals',
            webVitalsMeasurement: mappedCwv,
          });
        } else {
          mappedCwv.attribution = cwv.attribution;
          console.log(`[CWV:${collectionMode}]`, mappedCwv);
        }
      };

      const mapTraditional = (cwv: MetricWithAttribution) => mapToDataLayer(cwv, 'traditional');

      const mapPerNavigation = (cwv: MetricWithAttribution) =>
        mapToDataLayer(cwv, 'per_navigation');

      // Traditional full-page measurements
      onCLS(mapTraditional, { reportAllChanges: debugMode });
      onFCP(mapTraditional);
      onINP(mapTraditional, { reportAllChanges: debugMode });
      onLCP(mapTraditional);
      onTTFB(mapTraditional);

      // Per-navigation measurements, including the initial page and soft navigations
      onCLS(mapPerNavigation, {
        reportAllChanges: debugMode,
        reportSoftNavs: true,
      });
      onINP(mapPerNavigation, {
        reportAllChanges: debugMode,
        reportSoftNavs: true,
      });
      onLCP(mapPerNavigation, { reportSoftNavs: true });

      eventsAttached.current = true;
    }
  }, [cwvIndicator, eventsAttached]);

  return null;
}
